import { useState } from 'react';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import type { BulkResult } from '../types';
import { useGhiChungTu } from './useChungTu';

/** Chứng từ được nhắc tới trong hộp xác nhận: id và tên người dùng nhận ra (tên tệp). */
interface ChungTuNgan {
  id: string;
  ten: string;
}

type Viec =
  | { loai: 'xoa' | 'quet-lai' | 'huy-quet' | 'chuyen'; doc: ChungTuNgan; sauKhiXong?: () => void }
  | { loai: 'xoa-nhieu' | 'chuyen-nhieu'; ids: string[]; sauKhiXong?: () => void };

/**
 * Thao tác trên chứng từ, dùng chung cho danh sách và màn chi tiết.
 *
 * Việc phá huỷ hay không quay lại được (xoá, quét lại đè giá trị sửa tay, huỷ lượt quét, chuyển vào
 * kho tri thức) đi qua hộp xác nhận: tiêu đề đặt câu hỏi, thân nói hậu quả, nút nhắc lại việc; lỗi
 * hiện **trong** hộp, hộp không đóng. Việc nhẹ (xác nhận, thử lại bước suy luận) bấm thẳng, kết quả
 * báo toast. `409` sai trạng thái hiện nguyên câu backend — câu đó nói đúng trạng thái cần có.
 */
export function useThaoTacChungTu() {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const ghi = useGhiChungTu();
  const [viec, setViec] = useState<Viec | null>(null);
  const [loi, setLoi] = useState<string | null>(null);

  const mo = (v: Viec) => {
    setLoi(null);
    setViec(v);
  };
  const dong = () => setViec(null);
  const xong = (cau: string) => {
    toast.success(cau);
    const sau = viec?.sauKhiXong;
    dong();
    sau?.();
  };
  const loiTrongHop = (err: unknown) => setLoi(dichLoi(err));

  const baoHangLoat = (viecLam: 'xac-nhan' | 'chuyen' | 'xoa', kq: BulkResult) => {
    const so = kq.confirmed ?? kq.transferred ?? kq.deleted ?? 0;
    const tenViec = t(viecLam === 'xac-nhan' ? 'chungTu.viecXacNhan' : viecLam === 'chuyen' ? 'chungTu.viecChuyen' : 'chungTu.viecXoa');
    return kq.skipped.length > 0
      ? t('chungTu.ketQuaHangLoatBoQua', { viec: tenViec, xong: so, boQua: kq.skipped.length })
      : t('chungTu.ketQuaHangLoat', { viec: tenViec, xong: so });
  };

  const chay = () => {
    if (!viec) return;
    if ('ids' in viec) {
      const viecLam = viec.loai === 'xoa-nhieu' ? 'xoa' : 'chuyen';
      ghi.hangLoat.mutate(
        { viec: viecLam, ids: viec.ids },
        { onSuccess: (kq) => xong(baoHangLoat(viecLam, kq)), onError: loiTrongHop },
      );
    } else if (viec.loai === 'xoa') {
      ghi.xoa.mutate(viec.doc.id, { onSuccess: () => xong(t('chungTu.daXoaMot')), onError: loiTrongHop });
    } else if (viec.loai === 'quet-lai') {
      ghi.quetLai.mutate(
        { id: viec.doc.id, du: { reuse_transcript: false } },
        { onSuccess: () => xong(t('chungTu.daBatDauQuetLai')), onError: loiTrongHop },
      );
    } else if (viec.loai === 'huy-quet') {
      ghi.huyQuet.mutate(viec.doc.id, { onSuccess: () => xong(t('chungTu.daHuyQuet')), onError: loiTrongHop });
    } else {
      ghi.chuyen.mutate(viec.doc.id, { onSuccess: () => xong(t('chungTu.daChuyenMot')), onError: loiTrongHop });
    }
  };

  const dangChay =
    ghi.xoa.isPending || ghi.quetLai.isPending || ghi.huyQuet.isPending || ghi.chuyen.isPending || ghi.hangLoat.isPending;

  const noiDung = (() => {
    if (!viec) return null;
    switch (viec.loai) {
      case 'xoa':
        return {
          tieuDe: t('chungTu.xacNhanXoaMot', { ten: viec.doc.ten }),
          than: t('chungTu.xacNhanXoaMotMo'),
          nut: t('chungTu.xoaChungTu'),
          nguyHiem: true,
        };
      case 'quet-lai':
        return {
          tieuDe: t('chungTu.xacNhanQuetLai', { ten: viec.doc.ten }),
          than: t('chungTu.xacNhanQuetLaiMo'),
          nut: t('chungTu.quetLaiTuDau'),
          nguyHiem: false,
        };
      case 'huy-quet':
        return {
          tieuDe: t('chungTu.xacNhanHuyQuet', { ten: viec.doc.ten }),
          than: t('chungTu.xacNhanHuyQuetMo'),
          nut: t('chungTu.huyLuotQuet'),
          nguyHiem: true,
        };
      case 'chuyen':
        return {
          tieuDe: t('chungTu.xacNhanChuyenMot', { ten: viec.doc.ten }),
          than: t('chungTu.xacNhanChuyenMotMo'),
          nut: t('chungTu.chuyenVaoKho'),
          nguyHiem: false,
        };
      case 'xoa-nhieu':
        return {
          tieuDe: t('chungTu.xacNhanXoaSo', { so: viec.ids.length }),
          than: t('chungTu.xacNhanXoaSoMo'),
          nut: t('chungTu.xoaSo', { so: viec.ids.length }),
          nguyHiem: true,
        };
      case 'chuyen-nhieu':
        return {
          tieuDe: t('chungTu.xacNhanChuyenSo', { so: viec.ids.length }),
          than: t('chungTu.xacNhanChuyenSoMo'),
          nut: t('chungTu.chuyenSo', { so: viec.ids.length }),
          nguyHiem: false,
        };
    }
  })();

  return {
    moXoa: (doc: ChungTuNgan, sauKhiXong?: () => void) => mo({ loai: 'xoa', doc, sauKhiXong }),
    moQuetLai: (doc: ChungTuNgan) => mo({ loai: 'quet-lai', doc }),
    moHuyQuet: (doc: ChungTuNgan) => mo({ loai: 'huy-quet', doc }),
    moChuyen: (doc: ChungTuNgan) => mo({ loai: 'chuyen', doc }),
    moXoaNhieu: (ids: string[], sauKhiXong?: () => void) => mo({ loai: 'xoa-nhieu', ids, sauKhiXong }),
    moChuyenNhieu: (ids: string[], sauKhiXong?: () => void) => mo({ loai: 'chuyen-nhieu', ids, sauKhiXong }),
    xacNhan: (id: string) =>
      ghi.xacNhan.mutate(id, {
        onSuccess: () => toast.success(t('chungTu.daXacNhanMot')),
        onError: (e) => toast.error(dichLoi(e)),
      }),
    thuLaiSuyLuan: (id: string) =>
      ghi.quetLai.mutate(
        { id, du: { from_failed_step: true } },
        { onSuccess: () => toast.success(t('chungTu.daBatDauQuetLai')), onError: (e) => toast.error(dichLoi(e)) },
      ),
    xacNhanNhieu: (ids: string[], sauKhiXong?: () => void) =>
      ghi.hangLoat.mutate(
        { viec: 'xac-nhan', ids },
        {
          onSuccess: (kq) => {
            toast.success(baoHangLoat('xac-nhan', kq));
            sauKhiXong?.();
          },
          onError: (e) => toast.error(dichLoi(e)),
        },
      ),
    dangXacNhan: ghi.xacNhan.isPending || (ghi.hangLoat.isPending && !viec),
    hopThoai: (
      <HopXacNhanThaoTac
        open={Boolean(viec)}
        tieuDe={noiDung?.tieuDe ?? ''}
        than={noiDung?.than ?? ''}
        nhanXacNhan={noiDung?.nut ?? ''}
        nguyHiem={noiDung?.nguyHiem}
        dangChay={dangChay}
        loi={loi}
        onClose={dong}
        onXacNhan={chay}
      />
    ),
  };
}
